import { useState, useEffect, useMemo } from 'react';
import { useApi } from '../../contexts/ApiContext';
import { useToast } from '../../contexts/ToastContext';
import { PieChart } from '../Charts';
import CreateTicketModal from './CreateTicketModal';
import TicketDetailModal from './TicketDetailModal';
import TicketList from './TicketList';
import CustomLabelModal from './CustomLabelModal';
import { Printer } from 'lucide-react';

export default function SupportPanel() {
  const { api } = useApi();
  const { showError, showSuccess } = useToast();
  const [openTickets, setOpenTickets] = useState<any[]>([]);
  const [amphoraTickets, setAmphoraTickets] = useState<any[]>([]);
  const [closedTickets, setClosedTickets] = useState<any[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLabelModalOpen, setIsLabelModalOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const [openRes, amphoraRes, closedRes] = await Promise.all([
        api.get('/support?status=open'),
        api.get('/support?status=amphora'),
        api.get('/support?status=closed')
      ]);
      setOpenTickets(openRes.data || []);
      setAmphoraTickets(amphoraRes.data || []);
      setClosedTickets(closedRes.data || []);
    } catch (err: any) {
      console.error('Error loading tickets', err);
      setError('Error al cargar tickets. Por favor, recarga la página.');
      setOpenTickets([]);
      setAmphoraTickets([]);
      setClosedTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const handlePrint = async () => {
    try {
      setPrinting(true);
      const response = await api.get('/support/print/open', {
        responseType: 'blob'
      });

      // Verificar si la respuesta es un error (JSON en lugar de PDF)
      if (response.data instanceof Blob && response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        showError(errorData.error || 'Error al generar PDF');
        return;
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Abrir directamente en nueva ventana para impresión (más confiable en producción)
      const printWindow = window.open(url, '_blank');

      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            // Limpiar el blob URL después de un tiempo
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 1000);
          }, 500);
        };

        // Fallback si onload no se dispara
        setTimeout(() => {
          try {
            printWindow.print();
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 1000);
          } catch (error) {
            console.warn('Error al imprimir:', error);
            window.URL.revokeObjectURL(url);
          }
        }, 1000);

        showSuccess('Abriendo vista previa de impresión...');
      } else {
        // Si el popup fue bloqueado, intentar descargar
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'tickets-pendientes.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showSuccess('PDF descargado. Por favor, ábrelo para imprimir.');
      }

    } catch (err: any) {
      console.error('Error printing tickets', err);
      let errorMessage = 'Error al generar PDF';

      if (err.response?.data) {
        if (err.response.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const errorData = JSON.parse(text);
            errorMessage = errorData.error || errorMessage;
          } catch {
            errorMessage = err.message || errorMessage;
          }
        } else {
          errorMessage = err.response.data.error || errorMessage;
        }
      }

      showError(errorMessage);
    } finally {
      setPrinting(false);
    }
  };

  // Calcular estadísticas
  const stats = useMemo(() => {
    const allTickets = [...openTickets, ...amphoraTickets, ...closedTickets];

    // Número de tickets abiertos
    const openCount = openTickets.length;

    // Tiempo medio de resolución (solo tickets cerrados)
    let avgResolutionTime = 0;
    if (closedTickets.length > 0) {
      const resolutionTimes = closedTickets
        .filter(t => t.created_at && t.updated_at)
        .map(t => {
          const created = new Date(t.created_at).getTime();
          const updated = new Date(t.updated_at).getTime();
          return (updated - created) / (1000 * 60 * 60 * 24); // días
        });

      if (resolutionTimes.length > 0) {
        avgResolutionTime = resolutionTimes.reduce((a, b) => a + b, 0) / resolutionTimes.length;
      }
    }

    // Porcentaje por motivo
    const reasonCounts: { [key: string]: number } = {};
    allTickets.forEach(ticket => {
      const reason = ticket.reason || 'Sin motivo';
      reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
    });

    const reasonData = Object.entries(reasonCounts).map(([reason, count]) => ({
      label: `${reason} (${Math.round((count / allTickets.length) * 100)}%)`,
      value: count,
      reason
    }));

    // Porcentaje por responsable
    const responsibleCounts: { [key: string]: number } = {};
    allTickets.forEach(ticket => {
      const responsible = ticket.responsible || 'Sin responsable';
      responsibleCounts[responsible] = (responsibleCounts[responsible] || 0) + 1;
    });

    const responsibleData = Object.entries(responsibleCounts).map(([responsible, count]) => ({
      label: `${responsible} (${Math.round((count / allTickets.length) * 100)}%)`,
      value: count,
      responsible
    }));

    return {
      openCount,
      avgResolutionTime,
      reasonData,
      responsibleData,
      totalTickets: allTickets.length
    };
  }, [openTickets, closedTickets]);

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: 'var(--error)', marginBottom: '1rem' }}>{error}</p>
        <button className="btn btn-primary" onClick={loadTickets}>
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
        <button className="btn btn-secondary" onClick={() => setIsLabelModalOpen(true)}>
          <Printer size={16} style={{ marginRight: '0.5rem' }} /> Generar Etiquetas
        </button>
        <button className="btn btn-secondary" onClick={handlePrint} disabled={printing || openTickets.length === 0}>
          {printing ? 'Generando PDF...' : 'Imprimir Pendientes'}
        </button>
        <button className="btn btn-primary" onClick={() => setIsCreateModalOpen(true)}>
          + Nuevo Ticket
        </button>
      </div>

      {/* Estadísticas */}
      {!loading && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '1.5rem',
          marginBottom: '1rem'
        }}>
          {/* Número de tickets abiertos */}
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            padding: '1.5rem',
            borderRadius: '8px',
            border: '1px solid var(--border-color)'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', color: 'var(--text-secondary)' }}>
              Tickets Abiertos
            </h4>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--accent)' }}>
              {stats.openCount}
            </div>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
              {stats.totalTickets > 0 ? `${Math.round((stats.openCount / stats.totalTickets) * 100)}% del total` : 'Sin tickets'}
            </div>
          </div>

          {/* Tiempo medio de resolución */}
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            padding: '1.5rem',
            borderRadius: '8px',
            border: '1px solid var(--border-color)'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', color: 'var(--text-secondary)' }}>
              En Amphora
            </h4>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#8b5cf6' }}>
              {amphoraTickets.length}
            </div>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
              {stats.totalTickets > 0 ? `${Math.round((amphoraTickets.length / stats.totalTickets) * 100)}% del total` : 'Sin tickets'}
            </div>
          </div>

          {/* Porcentaje por motivo */}
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            padding: '1.5rem',
            borderRadius: '8px',
            border: '1px solid var(--border-color)'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', color: 'var(--text-secondary)' }}>
              Distribución por Motivo
            </h4>
            {stats.reasonData.length > 0 ? (
              <PieChart
                data={stats.reasonData.map((item, index) => ({
                  ...item,
                  color: ['#008060', '#f49342', '#2c6ecb', '#5c6ac4', '#47c1bf', '#dc3545'][index % 6]
                }))}
                size={180}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                No hay datos
              </div>
            )}
          </div>

          {/* Porcentaje por responsable */}
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            padding: '1.5rem',
            borderRadius: '8px',
            border: '1px solid var(--border-color)'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem', color: 'var(--text-secondary)' }}>
              Distribución por Responsable
            </h4>
            {stats.responsibleData.length > 0 ? (
              <PieChart
                data={stats.responsibleData.map((item, index) => ({
                  ...item,
                  color: ['#008060', '#f49342'][index % 2]
                }))}
                size={180}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                No hay datos
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <h3 style={{ marginBottom: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
          Tickets Abiertos
        </h3>
        {loading ? <div style={{ padding: '2rem', textAlign: 'center' }}>Cargando...</div> : (
          <TicketList
            tickets={openTickets || []}
            onSelect={id => setSelectedTicketId(id)}
            emptyMessage="No hay tickets abiertos"
            showDays={true}
          />
        )}
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
          En Amphora
        </h3>
        {loading ? <div style={{ padding: '2rem', textAlign: 'center' }}>Cargando...</div> : (
          <TicketList
            tickets={amphoraTickets || []}
            onSelect={id => setSelectedTicketId(id)}
            emptyMessage="No hay tickets en Amphora"
            showDays={true}
          />
        )}
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
          Histórico de Cerrados
        </h3>
        {loading ? <div style={{ padding: '2rem', textAlign: 'center' }}>Cargando...</div> : (
          <TicketList
            tickets={closedTickets || []}
            onSelect={id => setSelectedTicketId(id)}
            emptyMessage="No hay tickets cerrados"
            showDays={false}
            showNotified={true}
            onMarkNotified={async (id) => {
              try {
                await api.post(`/support/${id}/notified`);
                showSuccess('Ticket marcado como notificado');
                loadTickets();
              } catch (err: any) {
                showError(err.response?.data?.error || 'Error marcando como notificado');
              }
            }}
          />
        )}
      </div>

      <CreateTicketModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={loadTickets}
      />

      <TicketDetailModal
        isOpen={!!selectedTicketId}
        onClose={() => setSelectedTicketId(null)}
        ticketId={selectedTicketId}
        onUpdate={loadTickets}
      />

      <CustomLabelModal
        isOpen={isLabelModalOpen}
        onClose={() => setIsLabelModalOpen(false)}
      />
    </div>
  );
}
